import React, { useEffect, useState } from "react";
import {
  Button,
  Container,
  Dropdown,
  Grid,
  Icon,
  Search,
} from "semantic-ui-react";

import cx from "classnames";
import _ from "lodash";
import AccountSubscription from "components/features/Account/Management/Subscription";

import Users from "./List/Users";
import Invitations from "./List/Invitations";
import CreateUser from "./Create";

import styles from "./user_management.module.scss";
import SortFilter from "components/ui/SortFilter";
import { isAccountSuspended } from "components/features/Util/Util";
import { useSelector } from "react-redux";
import CSVReader from "./CreateCsv";
import InviteUser from "./InviteUser";
// sort filter dropdown options
const userSortOptions = [
  { key: 0, text: "Name", value: "first_name" },
  { key: 1, text: "Created Date", value: "created_date" },
  { key: 2, text: "Last Active Date", value: "last_activity_date" },
];

const invitationSortOptions = [
  { key: 0, text: "Invited Date", value: "invited_date" },
  { key: 1, text: "Email", value: "email" },
];



export default function UserManagement({ account, setGoBackPath }) {
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [searchText, setSearchText] = useState("");

  const [sort, setSort] = useState("first_name");
  const [order, setOrder] = useState("ASC");

  const [sortOptions, setSortOptions] = useState(userSortOptions);

  const [query, setQuery] = useState(null);
  const [type, setType] = useState("ALL");
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const permissionData = useSelector((state) => _.get(state, "users.me.role"));


  useEffect(() => {
    setGoBackPath("/");
  }, []);

  useEffect(() => {
    if (type === "invitations") {
      setOrder("ASC");
      setSort("invited_date");

      setSortOptions(invitationSortOptions);
    } else {
      setOrder("ASC");
      setSort("first_name");
      setSortOptions(userSortOptions);
    }

    setQuery(searchText);
  }, [type]);

  const onSetType = (type) => {
    setType(type);
  };

  const onSearchChange = (e) => {
    let value = e.target.value;
    if (!value) {
      setQuery(value);
    }

    setSearchText(value);
  };

  const onSearchClick = (e) => {
    e.preventDefault();
    setQuery(searchText);
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    setQuery(searchText);
  };

  const onSetSort = (sort) => {
    let changeOrder = order;
    if (sort === "name" || sort === "created_date") {
      changeOrder = "ASC";
    } else if (sort === "last_activity_date") {
      changeOrder = "DESC";
    }
    setSort(sort);
    setOrder(changeOrder);
  };

  const onSetOrder = (order) => {
    setOrder(order);
  };

  return (
    <>
      <Container className={cx(styles.container)}>
        <Grid>
          <Grid.Row columns={2}>
            <Grid.Column className={cx(styles.title)}>
              {account ? account.name : " "}
            </Grid.Column>
            <Grid.Column textAlign="right">
              <Button
                className={cx(styles.btnCreate)}
                onClick={() => setCreateUserOpen(!createUserOpen)}
                content={
                  <>
                    Create User{" "}
                    <span className={cx(styles.icon)}>
                      <Icon name="plus" />
                    </span>
                  </>
                }
                disabled={isAccountSuspended(account) || !_.get(permissionData, 'user.create', false)}
              />
              {type !== "invitations" && (<CSVReader/>)}
            </Grid.Column>
            {type === "invitations" && (<InviteUser/>)}
          </Grid.Row>

          <Grid.Row columns={2} className={cx(styles.tabs)}>
            <Grid.Column className={styles.filtersCol} width={10}>
              <FilterType
                title="All Users"
                total={total}
                setType={() => onSetType("ALL")}
                active={type === "ALL"}
                tabClass={styles.allUsers}
              />

              <FilterType
                title="Active Users"
                total={total}
                setType={() => onSetType("ACTIVE")}
                active={type === "ACTIVE"}
                tabClass={styles.activeUsers}
              />

              <FilterType
                title="Disabled Users"
                total={total}
                setType={() => onSetType("DISABLED")}
                active={type === "DISABLED"}
                tabClass={styles.diabledUsers}
              />

              <FilterType
                title="Invitations"
                total={total}
                setType={() => onSetType("invitations")}
                active={type === "invitations"}
                tabClass={styles.invitations}
              />
            </Grid.Column>
            <Grid.Column width={6} textAlign="right">
              <form onSubmit={onSearchSubmit} className={cx(styles.search)}>
                <Search
                  id="search"
                  input={{
                    icon: (
                      <Icon circular name="search" onClick={onSearchClick} />
                    ),
                    className: cx(styles.input),
                  }}
                  showNoResults={false}
                  value={searchText}
                  onSearchChange={onSearchChange}
                  placeholder="Search by name or email"
                ></Search>
              </form>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row columns={2} className={cx(styles.p0)}>
            <Grid.Column width={9}>
              {!loading && query && (
                <span>
                  {total} results for{" "}
                  <span className={styles.results}> "{query}" </span>
                </span>
              )}
            </Grid.Column>
            <Grid.Column
              width={7}
              textAlign="right"
              className={cx(styles.sort)}
            >
              <SortFilter
                sort={sort}
                order={order}
                options={sortOptions}
                onSortChange={onSetSort}
                onOrderChange={onSetOrder}
                className={cx(styles.dropdown)}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>

        {type === "invitations" && (
          <Invitations
            query={query}
            sort={sort}
            order={order}
            account={account}
            setTotal={setTotal}
            setLoading={setLoading}
          />
        )}

        {type !== "invitations" && (
          <Users
            type={type}
            query={query}
            sort={sort}
            order={order}
            account={account}
            setTotal={setTotal}
            setLoading={setLoading}
            permissionData={permissionData}
          />
        )}

        <CreateUser
          account={account}
          open={createUserOpen}
          setOpen={setCreateUserOpen}
        />
      </Container>
    </>
  );
}

function FilterType({ total, title, active, setType, tabClass }) {
  return (
    <>
      <div
        onClick={setType}
        className={cx(styles.tab, tabClass, active ? styles.active : "")}
      >
        <div className={cx(styles.title)}>{title}</div>
        <div className={cx(styles.count)}>{active && <> ({total})</>}</div>
      </div>
    </>
  );
}
